import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { BrandColors } from '../../global/helpers-marketing';
import { Labels } from '../../global/labels';
import { element } from '../../global/template-literals';
import { setAttributes } from '../../global/render-api';
import { UIIconColors } from '../icon/ui-icon.helpers';
import type {
    UISalesRibbonIconType,
    UISalesRibbonIconTypes,
} from './ui-salesribbon.type';
import type { NullOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-salesribbon.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISalesRibbon
 * @element ui-salesribbon
 * @classdesc Represents a class for <code>ui-salesribbon</code> element.
 * Marketing element for sales ribbon.
 * @property {UIIconGlyph} [icon] {@attr icon} Product icon glyph like in ui-icon.
 * @property {string} [salesribbonId] {@attr salesribbon-id} Salesribbon's ID for closable functionality
 * it will be sent to back system of ibank or any other.
 * @property {boolean} [closable] {@attr closable} add close functionality for sales ribbon.
 * @property {HTMLButtonElement|null} closeBtn Shortcut to close button if salesribbon has
 * 'closable' attribute.
 * @property {UISalesRibbonIconType} [iconType="product"] {@attr icon-type}
 *  {@desc product}
 *  {@desc image}
 *  {@desc badge}
 * @property {UIBrandColor | 'yellow'} [badgeColor="turquoise"] {@attr badge-color}
 * @property {('white' | 'orange' | 'turquoise' | 'lilac')} [glyphColor="white"] {@attr glyph-color}
 * Available only for the icon type product. Uses under the hood the <ui-icon size="avatar"></ui-icon>
 * @slot
 * @example
 * <ui-salesribbon icon="calculator" icon-type="badge">
 *   The best offer!
 * </ui-salesribbon>
 */
@Component({ tag: 'ui-salesribbon', styles })
export class UISalesRibbon extends UIElement {
    /**
     * @type {UISalesRibbonIconTypes}
     */
    static readonly IconTypes: UISalesRibbonIconTypes = {
        Product: 'product',
        Image: 'image',
        Badge: 'badge',
    };

    static readonly CloseEventName = 'salesribbon-close' as const;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-salesribbon', {
            close: 'Close',
        });
    }

    @Attribute(String) icon?: string;
    @Attribute(Boolean) closable?: boolean;
    @Attribute(String) salesribbonId?: string;
    @Attribute(String) iconType: UISalesRibbonIconType =
        UISalesRibbon.IconTypes.Product;
    @Attribute(String) glyphColor: string = UIIconColors.WHITE;
    @Attribute(String) badgeColor: string = BrandColors.Turquoise;

    @Child('.ui-salesribbon__close-button') closeBtn?: HTMLButtonElement;

    private onCloseHandler?: (event: MouseEvent) => void;

    /**
     * @private
     * @returns {HTMLDivElement | null}
     */
    private getIconElement(): NullOr<HTMLDivElement> {
        if (!this.icon) {
            return null;
        }

        const isIconBadge = this.iconType === UISalesRibbon.IconTypes.Badge;
        const badgeElement = isIconBadge
            ? element`<ui-badge color="${this.badgeColor}"></ui-badge>`
            : null;
        const isImageIcon = this.iconType === UISalesRibbon.IconTypes.Image;
        const iconElement = isImageIcon
            ? element`<ui-icon src="${this.icon}"></ui-icon>`
            : element`<ui-icon glyph="${this.icon}"></ui-icon>`;

        if (badgeElement) {
            badgeElement.append(iconElement);
        } else if (!isImageIcon) {
            setAttributes(iconElement, {
                size: 'avatar',
                ...(this.glyphColor ? { color: this.glyphColor } : undefined),
                bgcolor: this.badgeColor,
            });
        }

        return element`<div class="ui-salesribbon__icon-wrapper">${badgeElement || iconElement}</div>`;
    }

    /**
     * @private
     * @returns {HTMLDivElement | null}
     */
    private getCloseElement(): NullOr<HTMLDivElement> {
        if (!this.closable) {
            return null;
        }

        return this.createElement({
            tagName: 'div',
            classList: {
                'ui-salesribbon__close-wrapper': true,
            },
            children: [
                {
                    tagName: 'ui-button',
                    attributes: {
                        'type': 'button',
                        'layout': 'tertiary',
                        'glyph': 'redesign-x-mark',
                        'aria-label': UISalesRibbon.labels.close,
                        'title': UISalesRibbon.labels.close,
                    },
                    classList: {
                        'ui-salesribbon__close-button': true,
                    },
                },
            ],
        }) as HTMLDivElement;
    }

    /**
     * @private
     * @returns {boolean}
     */
    private canBeClosed(): boolean {
        return !!(this.closable && this.closeBtn);
    }

    /**
     * When salesribbon closed.
     * @private
     */
    private onClose(): void {
        this.dispatchCustomEvent(UISalesRibbon.CloseEventName, {
            id: this.salesribbonId,
        });
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const elements: Array<HTMLElement> = [];
        const iconElement = this.getIconElement();
        const closeElement = this.getCloseElement();
        const childrenElements: Array<HTMLElement> = Array.from(
            this.childNodes
        ).filter(
            (node) =>
                node.nodeType === Node.ELEMENT_NODE ||
                (node.nodeValue !== null && node.nodeValue.trim().length > 0)
        ) as Array<HTMLElement>;
        const lastChildTagName = childrenElements.at(-1)?.localName;
        const hasLinkElement = ['a', 'ui-link'].includes(
            lastChildTagName as string
        );
        const linkOutElement = hasLinkElement ? childrenElements.pop() : null;
        const areInlineChildrenElements = childrenElements.reduce(
            (isInline, childNode) =>
                childNode.nodeType === Node.TEXT_NODE ||
                childNode.tagName?.toLowerCase() === 'span',
            true
        );

        iconElement && elements.push(iconElement);
        childrenElements.length > 0 &&
            elements.push(
                element`<div class="ui-salesribbon__message${areInlineChildrenElements ? ' ui-salesribbon__simple-message' : ''}">${childrenElements}</div>`
            );
        closeElement && elements.push(closeElement);
        linkOutElement &&
            elements.push(
                element`<div class="ui-salesribbon__link-wrapper">${linkOutElement}</div>`
            );
        this.append(...elements);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn?.addEventListener('click', this.onCloseHandler);
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        if (this.canBeClosed()) {
            this.closeBtn?.removeEventListener('click', this.onCloseHandler!);
        }
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn?.addEventListener('click', this.onCloseHandler);
        }
    }
}
